import React, { useCallback, useEffect, useMemo, useRef, useState } from "react";
import SaveAltIcon from "@mui/icons-material/SaveAlt";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import CircularProgress from "@mui/material/CircularProgress";
import Stack from "@mui/material/Stack";
import Tab from "@mui/material/Tab";
import Tabs from "@mui/material/Tabs";
import Typography from "@mui/material/Typography";
import { toPng } from "html-to-image";
import ScreenshotLangPicker from "../../components/ScreenshotLangPicker";
import {
    appendScreenshotLang,
    screenshotSceneKey,
    useScreenshotTranslation,
} from "../../lib/screenshotI18n";
import { JIGSAW_MOBILE_TITLE, screenshotPath } from "../../prototype/mobile-jigsaw/routes";
import {
    isScreenshotSceneConfigured,
    JIGSAW_SCREENSHOT_SCENES,
} from "../../prototype/mobile-jigsaw/screenshotScenes";
import { jigsawShellBgForScene } from "../../prototype/mobile-jigsaw/styles/screenshotStyles";
import { downloadPngDataUrl } from "./downloadPngDataUrl";
import {
    persistGoogleAdFormatTab,
    readGoogleAdFormatTab,
} from "./googleAdFormatTabStorage";

/** Facebook / Instagram Ads image sizes. */
const AD_FORMATS = [
    { id: "square", label: "Square", width: 1200, height: 1200 },
    { id: "portrait45", label: "4:5", width: 1080, height: 1350 },
    { id: "vertical", label: "Vertical", width: 1080, height: 1920 },
];

const FORMAT_TAB_STORAGE_KEY = "jigsawFacebookAd.formatTab";

/** Match typical phone capture window for Jigsaw Muse screenshots. */
const PHONE_W = 430;
const PHONE_H = 940;
const PREVIEW_MAX_W = 180;
const PREVIEW_MAX_W_SHOWCASE = Math.round((PREVIEW_MAX_W * 2 * 1.5) / 2);
/** Giant / Gallery — stretch to fill portrait ad frame. */
const STRETCH_FILL_SLUGS = new Set(["05", "08"]);

const TO_PNG_OPTS = {
    cacheBust: true,
    pixelRatio: 1,
    skipFonts: true,
};

function usesStretchFill(scene) {
    return STRETCH_FILL_SLUGS.has(scene?.slug);
}

function screenshotPageHref(path, lang, format, imageOnly = false, portrait45Compact = false) {
    if (typeof window === "undefined") return path;
    const withLang = appendScreenshotLang(path, lang);
    const url = new URL(withLang, window.location.origin);
    if (format.id === "square") {
        url.searchParams.set("googleAdSquare", "1");
    }
    if (format.id === "landscape") {
        url.searchParams.set("googleAdLandscape", "1");
    }
    if (imageOnly) {
        url.searchParams.set("facebookAdImageOnly", "1");
    }
    if (portrait45Compact) {
        url.searchParams.set("facebookAdPortrait45", "1");
    }
    return url.toString();
}

async function loadImage(src) {
    const img = new Image();
    await new Promise((resolve, reject) => {
        img.onload = resolve;
        img.onerror = () => reject(new Error("Failed to decode capture"));
        img.src = src;
    });
    return img;
}

async function capturePhonePng(iframe) {
    const doc = iframe?.contentDocument;
    if (!doc?.documentElement) {
        throw new Error("Preview still loading — try again in a moment");
    }
    return toPng(doc.documentElement, {
        ...TO_PNG_OPTS,
        width: PHONE_W,
        height: PHONE_H,
        backgroundColor: null,
    });
}

async function fillWithCssBackground(ctx, width, height, background) {
    const mount = document.createElement("div");
    mount.setAttribute("aria-hidden", "true");
    mount.style.cssText = [
        "position:fixed",
        "left:-10000px",
        "top:0",
        `width:${width}px`,
        `height:${height}px`,
        `background:${background}`,
        "pointer-events:none",
    ].join(";");
    document.body.appendChild(mount);
    try {
        const url = await toPng(mount, {
            ...TO_PNG_OPTS,
            width,
            height,
        });
        const img = await loadImage(url);
        ctx.drawImage(img, 0, 0, width, height);
    } finally {
        document.body.removeChild(mount);
    }
}

function wrapCanvasText(ctx, text, maxWidth) {
    const words = String(text ?? "")
        .trim()
        .split(/\s+/)
        .filter(Boolean);
    if (words.length === 0) return [];
    const lines = [];
    let line = words[0];
    for (let i = 1; i < words.length; i += 1) {
        const next = `${line} ${words[i]}`;
        if (ctx.measureText(next).width <= maxWidth) {
            line = next;
        } else {
            lines.push(line);
            line = words[i];
        }
    }
    lines.push(line);
    return lines;
}

function drawPromoTitleBand(ctx, title, width, bandH, darkText = false) {
    if (!title) return;
    ctx.save();
    ctx.font = '800 48px system-ui, -apple-system, "Segoe UI", Roboto, Helvetica, Arial, sans-serif';
    ctx.textAlign = "center";
    ctx.textBaseline = "middle";
    if (darkText) {
        ctx.fillStyle = "#0f172a";
        ctx.shadowColor = "transparent";
        ctx.shadowBlur = 0;
        ctx.shadowOffsetY = 0;
    } else {
        ctx.fillStyle = "#f8fafc";
        ctx.shadowColor = "rgba(0,0,0,0.55)";
        ctx.shadowBlur = 14;
        ctx.shadowOffsetY = 2;
    }
    const lines = wrapCanvasText(ctx, title, width - 96);
    const lineH = 56;
    const blockH = lines.length * lineH;
    let y = (bandH - blockH) / 2 + lineH / 2;
    for (let i = 0; i < lines.length; i += 1) {
        ctx.fillText(lines[i], width / 2, y);
        y += lineH;
    }
    ctx.restore();
}

async function buildPortraitAdPngDataUrl(
    iframe,
    width,
    height,
    shellBg,
    stretchFill = false,
    coverFill = false,
    title = null,
    darkTitle = false,
    widthFill = false,
    coverShiftYRatio = 0,
) {
    const srcDataUrl = await capturePhonePng(iframe);
    const img = await loadImage(srcDataUrl);
    const canvas = document.createElement("canvas");
    canvas.width = width;
    canvas.height = height;
    const ctx = canvas.getContext("2d");
    if (!ctx) throw new Error("Canvas unavailable");

    await fillWithCssBackground(ctx, width, height, shellBg);

    if (stretchFill) {
        ctx.drawImage(img, 0, 0, width, height);
    } else if (coverFill) {
        // 05/08 — title band + image fills remaining (ratio kept, top-aligned).
        const titleBandH = title ? Math.round(height * 0.125) : 0;
        const areaTop = titleBandH;
        const areaH = height - titleBandH;
        if (titleBandH > 0) {
            drawPromoTitleBand(ctx, title, width, titleBandH, darkTitle);
        }
        const scale = Math.max(width / img.width, areaH / img.height);
        const drawW = img.width * scale;
        const drawH = img.height * scale;
        const shiftY = Math.round(areaH * coverShiftYRatio);
        ctx.drawImage(img, (width - drawW) / 2, areaTop + shiftY, drawW, drawH);
    } else if (widthFill) {
        // 4:5 other shots — full width, keep ratio, pin top (no side margins).
        const scale = Math.max(width / img.width, height / img.height);
        const drawW = img.width * scale;
        const drawH = img.height * scale;
        ctx.drawImage(img, (width - drawW) / 2, 0, drawW, drawH);
    } else {
        const scale = Math.min(width / img.width, height / img.height);
        const drawW = img.width * scale;
        const drawH = img.height * scale;
        ctx.drawImage(img, (width - drawW) / 2, (height - drawH) / 2, drawW, drawH);
    }
    return canvas.toDataURL("image/png");
}

async function buildShowcaseAdPngDataUrl(iframe, width, height) {
    const doc = iframe?.contentDocument;
    if (!doc?.documentElement) {
        throw new Error("Preview still loading — try again in a moment");
    }
    const frame =
        doc.querySelector('[data-google-ad-square="1"]') ||
        doc.querySelector('[data-google-ad-landscape="1"]') ||
        doc.documentElement;
    return toPng(frame, {
        ...TO_PNG_OPTS,
        width,
        height,
    });
}

function AdScreenshotTile({ format, scene, lang }) {
    const iframeRef = useRef(null);
    const [saving, setSaving] = useState(false);
    const [saveError, setSaveError] = useState(null);
    const [iframeReady, setIframeReady] = useState(false);
    const { t } = useScreenshotTranslation();

    const isShowcase = format.id === "square";
    const stretchFill = format.id === "vertical" && usesStretchFill(scene);
    const coverFill = format.id === "portrait45" && usesStretchFill(scene);
    const widthFill = format.id === "portrait45" && !usesStretchFill(scene);
    /** Giant /05 — nudge art up so the TV sits clearer in the 4:5 crop. */
    const coverShiftYRatio = coverFill && scene.slug === "05" ? -1 / 6 : 0;
    const href = screenshotPageHref(
        screenshotPath(scene.slug),
        lang,
        format,
        coverFill,
        widthFill,
    );
    const shellBg = jigsawShellBgForScene(scene);
    const titleKey = screenshotSceneKey("jigsaw", scene.slug, "headline");
    const coverTitle = (() => {
        const raw = t(titleKey);
        return raw && raw !== titleKey ? raw : scene.promoHeadline || scene.label;
    })();
    const darkTitle =
        scene.marketing === "gallery" || scene.layout === "twoColumnGrid";
    const titleBandH = coverFill ? Math.round(format.height * 0.125) : 0;
    const imageAreaH = format.height - titleBandH;
    const coverShiftY = Math.round(imageAreaH * coverShiftYRatio);
    const previewMaxW = isShowcase ? PREVIEW_MAX_W_SHOWCASE : PREVIEW_MAX_W;
    const previewScale = Math.min(1, previewMaxW / format.width);
    const previewW = Math.round(format.width * previewScale);
    const previewH = Math.round(format.height * previewScale);
    const fitScaleX = stretchFill
        ? format.width / PHONE_W
        : coverFill
          ? Math.max(format.width / PHONE_W, imageAreaH / PHONE_H)
          : widthFill
            ? Math.max(format.width / PHONE_W, format.height / PHONE_H)
            : Math.min(format.width / PHONE_W, format.height / PHONE_H);
    const fitScaleY = stretchFill ? format.height / PHONE_H : fitScaleX;

    useEffect(() => {
        setIframeReady(false);
    }, [href, format.id]);

    const handleDownload = useCallback(async () => {
        if (saving) return;
        setSaving(true);
        setSaveError(null);
        try {
            const dataUrl = isShowcase
                ? await buildShowcaseAdPngDataUrl(
                      iframeRef.current,
                      format.width,
                      format.height,
                  )
                : await buildPortraitAdPngDataUrl(
                      iframeRef.current,
                      format.width,
                      format.height,
                      shellBg,
                      stretchFill,
                      coverFill,
                      coverFill ? coverTitle : null,
                      darkTitle,
                      widthFill,
                      coverShiftYRatio,
                  );
            await downloadPngDataUrl(
                dataUrl,
                `jigsaw-muse-facebook-ad-${scene.slug}-${format.id}-${format.width}x${format.height}.png`,
            );
        } catch (e) {
            setSaveError(e?.message || "Failed to download PNG");
        } finally {
            setSaving(false);
        }
    }, [
        format,
        scene.slug,
        saving,
        shellBg,
        stretchFill,
        coverFill,
        coverTitle,
        darkTitle,
        widthFill,
        coverShiftYRatio,
        isShowcase,
    ]);

    return (
        <Box
            sx={{
                flex: `0 0 ${previewW}px`,
                width: previewW,
                mb: 1.5,
            }}
        >
            <Typography variant="subtitle2" sx={{ fontWeight: 700, mb: 0.25, fontSize: 12 }}>
                Screenshot {scene.slug}
            </Typography>
            <Typography
                variant="caption"
                color="text.secondary"
                sx={{
                    display: "block",
                    mb: 0.75,
                    minHeight: 28,
                    fontSize: 11,
                    lineHeight: 1.25,
                }}
            >
                {scene.label}
            </Typography>
            <Box
                sx={{
                    width: previewW,
                    height: previewH,
                    position: "relative",
                    overflow: "hidden",
                    borderRadius: 1,
                    border: 1,
                    borderColor: "divider",
                    boxShadow: 1,
                    background: isShowcase ? "#0f172a" : shellBg,
                }}
            >
                <Box
                    sx={{
                        width: format.width,
                        height: format.height,
                        transform: `scale(${previewScale})`,
                        transformOrigin: "top left",
                        overflow: "hidden",
                        position: "relative",
                        background: isShowcase ? "#0f172a" : shellBg,
                    }}
                >
                    {coverFill ? (
                        <Box
                            sx={{
                                position: "absolute",
                                left: 0,
                                top: 0,
                                width: format.width,
                                height: titleBandH,
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "center",
                                px: 6,
                                zIndex: 2,
                                pointerEvents: "none",
                            }}
                        >
                            <Typography
                                component="div"
                                sx={{
                                    m: 0,
                                    textAlign: "center",
                                    fontWeight: 800,
                                    fontSize: 48,
                                    lineHeight: 1.15,
                                    letterSpacing: "-0.02em",
                                    color: darkTitle ? "#0f172a" : "#f8fafc",
                                    textShadow: darkTitle
                                        ? "none"
                                        : "0 2px 14px rgba(0,0,0,0.55)",
                                }}
                            >
                                {coverTitle}
                            </Typography>
                        </Box>
                    ) : null}
                    <Box
                        component="iframe"
                        ref={iframeRef}
                        src={href}
                        title={`Screenshot ${scene.slug} · ${format.label}`}
                        loading="lazy"
                        onLoad={() => {
                            // Showcase loads cover images after mount — allow a short settle.
                            window.setTimeout(
                                () => setIframeReady(true),
                                isShowcase ? 2200 : 900,
                            );
                        }}
                        sx={
                            isShowcase
                                ? {
                                      position: "absolute",
                                      left: 0,
                                      top: 0,
                                      width: format.width,
                                      height: format.height,
                                      border: 0,
                                      pointerEvents: "none",
                                      display: "block",
                                      background: "#0f172a",
                                  }
                                : stretchFill
                                  ? {
                                        position: "absolute",
                                        left: 0,
                                        top: 0,
                                        width: PHONE_W,
                                        height: PHONE_H,
                                        border: 0,
                                        transform: `scale(${fitScaleX}, ${fitScaleY})`,
                                        transformOrigin: "top left",
                                        pointerEvents: "none",
                                        display: "block",
                                        background: "transparent",
                                    }
                                  : coverFill
                                    ? {
                                          position: "absolute",
                                          left: "50%",
                                          top: titleBandH + coverShiftY,
                                          width: PHONE_W,
                                          height: PHONE_H,
                                          border: 0,
                                          transform: `translateX(-50%) scale(${fitScaleX})`,
                                          transformOrigin: "top center",
                                          pointerEvents: "none",
                                          display: "block",
                                          background: "transparent",
                                      }
                                    : widthFill
                                      ? {
                                            position: "absolute",
                                            left: "50%",
                                            top: 0,
                                            width: PHONE_W,
                                            height: PHONE_H,
                                            border: 0,
                                            transform: `translateX(-50%) scale(${fitScaleX})`,
                                            transformOrigin: "top center",
                                            pointerEvents: "none",
                                            display: "block",
                                            background: "transparent",
                                        }
                                      : {
                                            position: "absolute",
                                            left: "50%",
                                            top: "50%",
                                            width: PHONE_W,
                                            height: PHONE_H,
                                            border: 0,
                                            transform: `translate(-50%, -50%) scale(${fitScaleX})`,
                                            transformOrigin: "center center",
                                            pointerEvents: "none",
                                            display: "block",
                                            background: "transparent",
                                        }
                        }
                    />
                </Box>
            </Box>
            <Stack spacing={0.5} sx={{ mt: 0.75 }}>
                <Button
                    size="small"
                    variant="contained"
                    startIcon={
                        saving ? <CircularProgress size={14} color="inherit" /> : <SaveAltIcon />
                    }
                    onClick={handleDownload}
                    disabled={saving || !iframeReady}
                    sx={{ minHeight: 36, textTransform: "none", fontWeight: 700 }}
                >
                    {saving ? "Saving…" : "Download"}
                </Button>
                {saveError ? (
                    <Typography variant="caption" color="error">
                        {saveError}
                    </Typography>
                ) : null}
            </Stack>
        </Box>
    );
}

/**
 * Admin page — Facebook Ads creatives from all Jigsaw Muse screenshots
 * in Square (1:1) / Vertical (9:16), with per-shot download.
 */
export default function JigsawFacebookAdPage() {
    const { lang } = useScreenshotTranslation();
    const [formatTab, setFormatTab] = useState(() =>
        readGoogleAdFormatTab(AD_FORMATS, FORMAT_TAB_STORAGE_KEY),
    );
    const scenes = useMemo(
        () => JIGSAW_SCREENSHOT_SCENES.filter(isScreenshotSceneConfigured),
        [],
    );
    const activeFormat = AD_FORMATS[formatTab] ?? AD_FORMATS[0];

    useEffect(() => {
        persistGoogleAdFormatTab(AD_FORMATS, FORMAT_TAB_STORAGE_KEY, formatTab);
    }, [formatTab]);

    return (
        <Box sx={{ p: 2, pb: 6, maxWidth: 1400, mx: "auto" }}>
            <Typography variant="h5" component="h1" sx={{ fontWeight: 700, mb: 0.5 }}>
                {JIGSAW_MOBILE_TITLE} — Facebook Ads
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                All screenshots (01–{String(scenes.length).padStart(2, "0")}) · Square (1:1), 4:5 &amp;
                Vertical (9:16) · download PNG under each preview
            </Typography>
            <ScreenshotLangPicker sx={{ mb: 2 }} />

            <Tabs
                value={formatTab}
                onChange={(_, next) => setFormatTab(next)}
                variant="scrollable"
                scrollButtons="auto"
                sx={{
                    mb: 2,
                    borderBottom: 1,
                    borderColor: "divider",
                    minHeight: 48,
                }}
            >
                {AD_FORMATS.map((format, index) => (
                    <Tab
                        key={format.id}
                        label={`${format.label} · ${format.width}×${format.height}`}
                        id={`jigsaw-facebook-ad-tab-${index}`}
                        aria-controls={`jigsaw-facebook-ad-tabpanel-${index}`}
                        sx={{ minHeight: 48, textTransform: "none", fontWeight: 600 }}
                    />
                ))}
            </Tabs>

            <Box
                role="tabpanel"
                id={`jigsaw-facebook-ad-tabpanel-${formatTab}`}
                aria-labelledby={`jigsaw-facebook-ad-tab-${formatTab}`}
            >
                <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                    {activeFormat.id === "square"
                        ? `Square 1:1 (${activeFormat.width}×${activeFormat.height}) — best for Facebook Feed and Instagram Feed`
                        : activeFormat.id === "portrait45"
                          ? `4:5 (${activeFormat.width}×${activeFormat.height}) — tallest feed ratio without Mobile News Feed masking`
                          : `Vertical 9:16 (${activeFormat.width}×${activeFormat.height}) — best for Instagram Stories, Facebook Stories, and Reels`}
                </Typography>
                <Box
                    sx={{
                        display: "flex",
                        flexWrap: "wrap",
                        gap: 1.5,
                        alignItems: "flex-start",
                    }}
                >
                    {scenes.map((scene) => (
                        <AdScreenshotTile
                            key={`${activeFormat.id}-${scene.slug}`}
                            format={activeFormat}
                            scene={scene}
                            lang={lang}
                        />
                    ))}
                </Box>
            </Box>
        </Box>
    );
}
